// src/pages/CartPage.jsx
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { ref, uploadBytesResumable, getDownloadURL, storage } from "../services/mysqlStorage";
import { saveCartForFollowUp } from "../services/savedCart";
import "./CartPage.css";

const MAX_FILE_SIZE_MB = 50;

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const PrinterIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9"/>
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
    <rect x="6" y="14" width="12" height="8"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const TruckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
    <circle cx="7" cy="21" r="2"/><path d="M9 21h6"/><circle cx="17" cy="21" r="2"/>
    <path d="M17 3v5h2a2 2 0 0 1 2 2v7"/>
  </svg>
);

const NoteIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);

const PaletteIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/>
    <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/>
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
  </svg>
);

const ZapIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>
  </svg>
);

export default function CartPage() {
  var { items, removeItem, totalItems, totalPrice, setArtworkUrl } = useCart();
  var { user, profile } = useAuth();
  var navigate = useNavigate();

  var [uploads, setUploads] = useState({}); // { [cartId]: { progress, error } }
  var [contact, setContact] = useState({
    fullName: (profile && profile.fullName) || (user && user.displayName) || "",
    email: (user && user.email) || "",
    phone: (profile && profile.phone) || "",
  });
  var [sendStatus, setSendStatus] = useState(""); // "" | "sending" | "sent" | "error"
  var [sendError, setSendError] = useState("");

  function handleUpload(item, fileList) {
    var file = fileList[0];
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) { setUploads(function (p) { return { ...p, [item.cartId]: { error: "Only PDF files are accepted." } }; }); return; }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) { setUploads(function (p) { return { ...p, [item.cartId]: { error: "File too large (max 50MB)." } }; }); return; }
    setUploads(function (p) { return { ...p, [item.cartId]: { progress: 0 } }; });
    var task = uploadBytesResumable(ref(storage, "artwork/" + (user ? user.uid : "guest") + "/" + Date.now() + "_" + file.name), file);
    task.on(
      "state_changed",
      function (snap) {
        var pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        setUploads(function (p) { return { ...p, [item.cartId]: { progress: pct } }; });
      },
      function (err) { setUploads(function (p) { return { ...p, [item.cartId]: { error: "Upload failed" + (err && err.message ? ": " + err.message : ".") } }; }); },
      function () {
        getDownloadURL(task.snapshot.ref).then(function (url) {
          setArtworkUrl(item.cartId, url, file.name);
          setUploads(function (p) { var c = { ...p }; delete c[item.cartId]; return c; });
        });
      }
    );
  }

  async function handleSendToTeam(e) {
    e.preventDefault();
    setSendError("");
    if (!contact.fullName.trim()) { setSendError("Please enter your name."); return; }
    if (!/^\S+@\S+\.\S+$/.test(contact.email.trim())) { setSendError("Please enter a valid email."); return; }
    setSendStatus("sending");
    var ok = await saveCartForFollowUp({ customer: contact, items: items });
    setSendStatus(ok ? "sent" : "error");
    if (!ok) setSendError("Could not send your cart. Please try again.");
  }

  var anyUploading = Object.keys(uploads).some(function (k) { return uploads[k].progress !== undefined; });

  function handleCheckout() {
    if (!user) {
      navigate("/login", { state: { from: "/checkout" } });
    } else {
      navigate("/checkout");
    }
  }

  if (items.length === 0) {
    return (
      <div className="cart-page">
        <div className="container cart-empty">
          <div className="cart-empty-icon"><PrinterIcon /></div>
          <h2>Your cart is empty</h2>
          <p>Browse our products and add items to get started.</p>
          <Link to="/products" className="btn-primary">Browse Products</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">

        {/* Header */}
        <div className="cart-header">
          <h1>Your Cart <span className="cart-count-badge">{totalItems} item{totalItems !== 1 ? "s" : ""}</span></h1>
          <Link to="/products" className="cart-continue-link">← Continue Shopping</Link>
        </div>

        <div className="cart-layout">
          {/* Cart Items */}
          <div className="cart-items">
            {items.map(function(item, idx) {
              return (
                <div key={item.cartId} className="cart-item anim-fade-in-up" style={{ animationDelay: (idx * 0.05) + "s" }}>
                  {/* Product Image */}
                  <div className="cart-item-img">
                    {item.imageUrl
                      ? <img src={item.imageUrl} alt={item.productName} />
                      : <div className="cart-item-img-placeholder"><PrinterIcon /></div>
                    }
                  </div>

                  {/* Product Info */}
                  <div className="cart-item-info">
                    <span className="cart-item-category">{item.category}</span>
                    <h3 className="cart-item-name">{item.productName}</h3>
                    {item.description && (
                      <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2, marginBottom: 4, lineHeight: 1.5 }}>{item.description}</p>
                    )}
                    <div className="cart-item-options">
                      {item.quantity  && <span>Qty: <strong>{Number(item.quantity).toLocaleString()}</strong></span>}
                      {item.size      && <span>Size: <strong>{item.size}</strong></span>}
                      {item.finish    && <span>Finish: <strong>{item.finish}</strong></span>}
                      {item.turnaround && <span>Turnaround: <strong>{item.turnaround}</strong></span>}
                    </div>
                    {item.notes && (
                      <p className="cart-item-notes"><NoteIcon /> {item.notes}</p>
                    )}
                    <div style={{ marginTop: 8, fontSize: 13 }}>
                      {item.artworkUrl ? (
                        <span style={{ color: "#16a34a" }}>
                          ✓ Artwork uploaded{item.artworkName ? ": " + item.artworkName : ""}{" "}
                          <button type="button" onClick={function () { setArtworkUrl(item.cartId, null); }} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 12 }}>Remove</button>
                        </span>
                      ) : uploads[item.cartId] && uploads[item.cartId].progress !== undefined ? (
                        <span>Uploading… {uploads[item.cartId].progress}%</span>
                      ) : (
                        <label style={{ cursor: "pointer", color: "#00aadd", fontWeight: 600 }}>
                          📎 Upload artwork (PDF, max {MAX_FILE_SIZE_MB}MB)
                          <input type="file" accept=".pdf" style={{ display: "none" }}
                            onChange={function (e) { handleUpload(item, e.target.files); e.target.value = ""; }} />
                        </label>
                      )}
                      {uploads[item.cartId] && uploads[item.cartId].error && (
                        <div style={{ color: "#dc2626", fontSize: 12 }}>{uploads[item.cartId].error}</div>
                      )}
                    </div>
                  </div>

                  {/* Price + Remove */}
                  <div className="cart-item-right">
                    <div className="cart-item-price">
                      {item.price > 0 ? (
                        <span>From <strong>${item.price.toFixed(2)}</strong></span>
                      ) : (
                        <span className="cart-price-tbc">Price TBC</span>
                      )}
                    </div>
                    <button className="cart-remove-btn" onClick={function() { removeItem(item.cartId); }} title="Remove item">
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="cart-summary">
            <div className="cart-summary-box">
              <h3>Order Summary</h3>

              <div className="cart-summary-rows">
                {items.map(function(item) {
                  return (
                    <div key={item.cartId} className="summary-row">
                      <span>{item.productName} × {Number(item.quantity).toLocaleString() || 1}</span>
                      <span>{item.price > 0 ? "$" + item.price.toFixed(2) : "TBC"}</span>
                    </div>
                  );
                })}
              </div>

              <div className="cart-summary-divider" />

              <div className="summary-row summary-total">
                <span>Estimated Total</span>
                <span>{totalPrice > 0 ? "$" + totalPrice.toFixed(2) : "TBC"}</span>
              </div>

              <p className="cart-summary-note">
                 Final price confirmed after artwork review. HST may apply.
              </p>

              <button
                className="btn-primary cart-checkout-btn"
                onClick={handleCheckout}
              >
                {user ? "Proceed to Checkout →" : "Sign In to Checkout →"}
              </button>

              <div className="cart-trust-badges">
                <div className="trust-badge"><ShieldIcon /> Secure checkout</div>
                <div className="trust-badge"><TruckIcon /> Canada-wide delivery</div>
              </div>
            </div>

            <div className="cart-info-box">
              <p><strong>Not ready to pay?</strong> Send your cart and artwork to our team and we'll contact you directly to finish your order.</p>
              {sendStatus === "sent" ? (
                <p style={{ color: "#16a34a", fontWeight: 600 }}>✓ Sent! We'll follow up at {contact.email}.</p>
              ) : (
                <form onSubmit={handleSendToTeam} style={{ display: "grid", gap: 8 }}>
                  <input placeholder="Full name *" value={contact.fullName} onChange={function (e) { setContact({ ...contact, fullName: e.target.value }); }} />
                  <input type="email" placeholder="Email *" value={contact.email} onChange={function (e) { setContact({ ...contact, email: e.target.value }); }} />
                  <input placeholder="Phone" value={contact.phone} onChange={function (e) { setContact({ ...contact, phone: e.target.value }); }} />
                  {sendError && <span style={{ color: "#dc2626", fontSize: 12 }}>{sendError}</span>}
                  <button type="submit" className="btn-outline" disabled={sendStatus === "sending" || anyUploading}>
                    {sendStatus === "sending" ? "Sending..." : anyUploading ? "Wait for upload..." : "Send cart & artwork to team"}
                  </button>
                </form>
              )}
            </div>

            {/* Promo / Info */}
            <div className="cart-info-box">
              <p><span className="cart-info-icon"><PaletteIcon /></span> <strong>Free Artwork Check</strong> — our team reviews every file before printing.</p>
              <p><span className="cart-info-icon"><ZapIcon /></span> <strong>Same-Day Dispatch</strong> available on orders placed before 2pm.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
